import { useState } from 'react';
import { Bold, Italic, Palette, Type, RotateCcw } from 'lucide-react';

/**
 * Toolbar flotante para editar formato de textos en slides
 * Soporta: negrita, cursiva, tamaño de fuente, color de texto
 */
const EditorToolbar = ({ onFormat, onClose, selectedElement: _selectedElement }) => {
  const [expandedSection, setExpandedSection] = useState(null);

  const handleFormat = (type, value) => {
    onFormat(type, value);
  };

  const handleReset = () => {
    onFormat('reset', null);
  };

  const fontSizes = [
    { label: 'Pequeño', value: '12px' },
    { label: 'Normal', value: '16px' },
    { label: 'Grande', value: '20px' },
    { label: 'Muy Grande', value: '28px' },
    { label: 'Título', value: '36px' },
  ];

  const textColors = [
    { label: 'Negro', value: '#000000' },
    { label: 'Gris', value: '#666666' },
    { label: 'Azul', value: '#0F31F7' },
    { label: 'Verde', value: '#10B981' },
    { label: 'Naranja', value: '#F59E0B' },
    { label: 'Rojo', value: '#EF4444' },
    { label: 'Morado', value: '#8B5CF6' },
  ];

  return (
    <div className="fixed top-20 left-1/2 -translate-x-1/2 z-50 bg-white rounded-xl shadow-2xl border border-gray-200 p-3 max-w-2xl">
      <div className="flex items-center gap-2 flex-wrap">
        <button
          onClick={() => handleFormat('bold')}
          className="p-2 hover:bg-gray-100 rounded transition-colors"
          title="Negrita (Ctrl+B)"
          aria-label="Negrita"
        >
          <Bold className="w-4 h-4 text-gray-700" />
        </button>
        <button
          onClick={() => handleFormat('italic')}
          className="p-2 hover:bg-gray-100 rounded transition-colors"
          title="Cursiva (Ctrl+I)"
          aria-label="Cursiva"
        >
          <Italic className="w-4 h-4 text-gray-700" />
        </button>

        <div className="h-6 w-px bg-gray-300" />

        {/* Dropdown Tamaño de Fuente */}
        <div className="relative">
          <button
            onClick={() =>
              setExpandedSection(
                expandedSection === 'fontSize' ? null : 'fontSize'
              )
            }
            className="flex items-center gap-1 px-2 py-1.5 hover:bg-gray-100 rounded text-sm text-gray-700 transition-colors"
          >
            <Type className="w-4 h-4" />
            <span className="text-xs font-medium">Tamaño</span>
          </button>

          {expandedSection === 'fontSize' && (
            <div className="absolute top-full left-0 mt-1 bg-white border border-gray-200 rounded-lg shadow-lg py-1 min-w-[120px]">
              {fontSizes.map((size) => (
                <button
                  key={size.value}
                  onClick={() => {
                    handleFormat('fontSize', size.value);
                    setExpandedSection(null);
                  }}
                  className="w-full px-3 py-1.5 text-left text-sm hover:bg-gray-100 transition-colors"
                >
                  {size.label}
                </button>
              ))}
            </div>
          )}
        </div>

        {/* Dropdown Color */}
        <div className="relative">
          <button
            onClick={() =>
              setExpandedSection(expandedSection === 'color' ? null : 'color')
            }
            className="flex items-center gap-1 px-2 py-1.5 hover:bg-gray-100 rounded text-sm text-gray-700 transition-colors"
          >
            <Palette className="w-4 h-4" />
            <span className="text-xs font-medium">Color</span>
          </button>

          {expandedSection === 'color' && (
            <div className="absolute top-full left-0 mt-1 bg-white border border-gray-200 rounded-lg shadow-lg p-2 min-w-[160px]">
              <div className="grid grid-cols-4 gap-1">
                {textColors.map((color) => (
                  <button
                    key={color.value}
                    onClick={() => {
                      handleFormat('color', color.value);
                      setExpandedSection(null);
                    }}
                    className="w-8 h-8 rounded border border-gray-300 hover:border-gray-400 transition-colors"
                    style={{ backgroundColor: color.value }}
                    title={color.label}
                  />
                ))}
              </div>
              {/* Input color custom */}
              <div className="mt-2 pt-2 border-t border-gray-200">
                <label className="text-xs text-gray-600 block mb-1">
                  Personalizado
                </label>
                <input
                  type="color"
                  onChange={(e) => {
                    handleFormat('color', e.target.value);
                  }}
                  className="w-full h-8 rounded cursor-pointer border border-gray-300"
                />
              </div>
            </div>
          )}
        </div>

        {/* Separador */}
        <div className="h-6 w-px bg-gray-300" />

        <div className="ml-auto flex items-center gap-2">
          <button
            onClick={handleReset}
            className="px-2 py-1 text-xs font-medium text-gray-600 hover:bg-gray-100 rounded transition-colors flex items-center gap-1"
            title="Restablecer estilos"
          >
            <RotateCcw className="w-3.5 h-3.5" />
            <span>Restablecer</span>
          </button>
          <button
            onClick={onClose}
            className="px-2 py-1 text-xs font-medium text-gray-600 hover:bg-gray-100 rounded transition-colors"
          >
            ✕ Cerrar
          </button>
        </div>
      </div>
    </div>
  );
};

export default EditorToolbar;
